import React, { useCallback } from 'react';
import { FormGroup } from '~/components/form';
import { AiFillDollarCircle } from 'react-icons/ai';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave } from '../../hooks';
import sectionTypes from '../sectionTypes';

/**
 * @typedef TaxesAndSpecialAssesmentsValues
 * @property {number}   sectionId
 * @property {string}   taxesPaidThrough
 * @property {string}   taxesNotDue
 * @property {string}   taxesNowDuePayable
 * @property {string}   parcelId
 * @property {[object]} initialDefaultData
 * @typedef FormState
 * @property {TaxesAndSpecialAssesmentsValues} values The current form values
 */

/** @type {TaxesAndSpecialAssesmentsValues} */
const defaultState = {
    sectionId: 0,
    orderNumber: null,
    taxesPaidThrough: '',
    taxesNotDue: '',
    taxesNowDuePayable: '',
    parcelId: '',
    initialDefaultData: {}
};



const loadData = async (values) => {

    return {
        ...values,        
        orderNumber : values.orderNumber === '' ? null : values.orderNumber,
        initialDefaultData: {}
    };
};

const TaxesAndSpecialAssesments = ({ titleOpinionId, readOnly }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.TAXES_ASSESMENTS, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values)
            .then(v => loadSection(v));
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {TaxesAndSpecialAssesmentsValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const { saveField } = useAutoSave(sectionTypes.TAXES_ASSESMENTS, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);
   
    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><AiFillDollarCircle size="45%"/></div>
                        <div className="ml-2"> Taxes &amp; Special Assessments </div>
                    </div> 
                    
                </div>
            </div>
            <div className="text-sm mb-2">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>            
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm bold mb-4">
                
                <FormGroup
                    label="Taxes have been paid through and include those for the year: "
                    required
                    readOnly={readOnly}
                    autoComplete='no'
                    groupClassName="col-span-6 mt-3 mb-8"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='taxesPaidThrough'                 
                    value={state.values.taxesPaidThrough}
                    error={state.errors.taxesPaidThrough}
                    {...fieldActions}
                    onBlur={onBlur}
                />
                
                <FormGroup
                    label="Taxes, a lien, deferred, or otherwise, but not yet due and payable: "
                    readOnly={readOnly}
                    autoComplete='no'
                    groupClassName="col-span-6 mt-3 mb-8"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='taxesNotDue'                 
                    value={state.values.taxesNotDue}
                    error={state.errors.taxesNotDue}
                    {...fieldActions}
                    onBlur={onBlur}
                />
                
                <FormGroup
                    label="Taxes now due and payable in the amount of: "
                    groupClassName="col-span-6 mt-4 mb-8"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='taxesNowDuePayable' 
                    readOnly={readOnly}        
                    autoComplete='no'        
                    value={state.values.taxesNowDuePayable}
                    error={state.errors.taxesNowDuePayable}
                    {...fieldActions}
                    onBlur={onBlur}
                />
                
                <FormGroup
                    label="Parcel ID/PIN/Tax Map ID: "
                    groupClassName="col-span-6 mt-4 mb-8"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='parcelId'
                    readOnly={readOnly}  
                    autoComplete='no'               
                    value={state.values.parcelId}
                    error={state.errors.parcelId}
                    {...fieldActions}
                    onBlur={onBlur}
                />
            </div>
        </div>
    );
};

export default TaxesAndSpecialAssesments;